const apiUrl = import.meta.env.VITE_API_URL;

interface FetchFastApiParams {
  body?: Record<string, string | number | object> | FormData;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  retryNumber?: number;
  query?: string | Record<string, string> | URLSearchParams | string[][] | undefined;
}

export const refetchAccessToken = async () => {
  const idToken = localStorage.getItem("id_token");
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken || !idToken) {
    return false;
  }

  const res = await fetchFastApi({
    method: "POST",
    body: { refresh_token: refreshToken, id_token: idToken },
    path: "/auth/refresh-tokens",
  });
  if (res.access_token) {
    localStorage.setItem("access_token", res.access_token);
    localStorage.setItem("id_token", res.id_token);
    return true;
  }
  return false;
};

export const fetchFastApi = async ({ body, method, path, query, retryNumber = 0 }: FetchFastApiParams) => {
  let url = `${apiUrl}${path}`;
  const token = localStorage.getItem("access_token");
  const workspaceId = localStorage.getItem("workspace_id");

  try {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      "X-Workspace-Id": workspaceId || "",
    };

    let requestBody: string | FormData | null = null;

    if (method !== "GET" && body) {
      if (body instanceof FormData) {
        requestBody = body;
        // Don't set Content-Type for FormData, let the browser set it
      } else {
        headers["Content-Type"] = "application/json";
        requestBody = JSON.stringify(body);
      }
    }
    if (method === "GET" && query) {
      const queryString = new URLSearchParams(query).toString();
      url = url.concat(`?${queryString}`);
    }

    const response = await fetch(url, {
      method,
      headers,
      body: requestBody,
    });

    const contentType = response.headers.get("content-type");

    if (!response.ok) {
      if (response.status === 401) {
        const body = await response.json();
        if (body.detail.refetchToken) {
          const refetchResponse = await refetchAccessToken();
          if (refetchResponse && retryNumber < 2) {
            const retriedResponse: string = await fetchFastApi({
              body,
              method,
              path,
              retryNumber: retryNumber + 1,
            });
            return retriedResponse;
          }
        }
      }
      if (response.status === 402) {
        // redirect to billing page
        window.location.href = "/dashboard/out-of-credits";
      }

      throw new Error(
        JSON.stringify({
          status: response.status,
        })
      );
    }

    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else if (
      contentType &&
      (contentType.includes("application/octet-stream") ||
        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"))
    ) {
      return await response.blob();
    } else {
      return await response.text();
    }
  } catch (e) {
    console.error(e);
    throw e;
  }
};
